import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from "react";
import { Button, Form, Tab, Tabs } from "react-bootstrap";
import useOutsideCallback from "../../hooks/UseOutsideCallback";

import FeatureFlag from "./FeatureFlag";
import FeatureFlagAssignmentTable, {
  FeatureFlagAssignmentRow,
} from "./FeatureFlagAssignmentTable";
import FormatFeatureFlagDate from "./FormatFeatureFlagDate";

/** Default / clamp sizes for the overlay details panel (px). */
export const FEATURE_FLAG_PANEL_WIDTH_DEFAULT_PX = 672; // 42rem
export const FEATURE_FLAG_PANEL_WIDTH_MIN_PX = 384; // 24rem
export const FEATURE_FLAG_PANEL_WIDTH_MAX_PX = 768; // 48rem

interface FeatureFlagDetailsPanelProps {
  flag: FeatureFlag;
  /** Confirm-pending enabled value; null means use the flag's loaded enabled. */
  pendingEnabled?: boolean | null;
  /** Same handler as the main table so panel and table stay in sync. */
  onToggleEnabled: (id: number, enabled: boolean) => void;
  /** Opens the same "not available yet" message used by Add. */
  onEditFlag: () => void;
  /**
   * Starts the confirm-then-DELETE flow removing the flag from the selected
   * tenant: its ConditionalFeatureFlagTenant row (when present) and all of
   * the tenant's facility rows. Same action as the main-table remove control.
   */
  onDeleteFlagForTenant: (flagId: number) => void;
  /**
   * Starts the confirm-then-DELETE flow for the selected tenant's
   * ConditionalFeatureFlagTenant row (the row in the Tenants tab).
   */
  onDeleteTenantRow: (flagId: number) => void;
  /**
   * Starts the confirm-then-DELETE flow for one flag<=>facility row
   * (ConditionalFeatureFlagFacility) in the Facilities tab. Receives the
   * facility id, not the row id.
   */
  onDeleteFacilityRow: (flagId: number, facilityId: number) => void;
  /** Closes the panel (clear selection). */
  onClose: () => void;
  /** Current panel width in pixels (shared with the layout CSS variable). */
  widthPx: number;
  /** Called while the left-edge resize handle is dragged or keyed. */
  onWidthChange: (widthPx: number) => void;
  /** Called when a click is made outside of the panel */
  onOutsideCallback: (event: MouseEvent | TouchEvent) => void;
}

function clampPanelWidth(widthPx: number): number {
  return Math.min(
    FEATURE_FLAG_PANEL_WIDTH_MAX_PX,
    Math.max(FEATURE_FLAG_PANEL_WIDTH_MIN_PX, widthPx)
  );
}

/**
 * Details panel shown next to the feature flag table when a row is selected.
 * Shows the flag's details, audit dates, and the enabled toggle; Edit opens
 * the placeholder message until flag editing is implemented. The left edge
 * can be dragged (or focused + arrow keys) to resize the panel.
 */
function FeatureFlagDetailsPanel({
  flag,
  pendingEnabled = null,
  onToggleEnabled,
  onEditFlag,
  onDeleteFlagForTenant,
  onDeleteTenantRow,
  onDeleteFacilityRow,
  onClose,
  widthPx,
  onWidthChange,
  onOutsideCallback,
}: FeatureFlagDetailsPanelProps) {
  const panelRef = useRef(null);
  const resizeStartRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(
    null
  );
  // Coalesce drag updates to one width commit per animation frame (React 17
  // does not batch pointer-move setState the way React 18+ does).
  const pendingWidthRef = useRef<number | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const onWidthChangeRef = useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;

  const cancelPendingWidthFrame = useCallback(() => {
    if (resizeRafRef.current != null) {
      cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = null;
    }
  }, []);

  const flushPendingWidth = useCallback(() => {
    cancelPendingWidthFrame();
    if (pendingWidthRef.current == null) {
      return;
    }

    onWidthChangeRef.current(pendingWidthRef.current);
    pendingWidthRef.current = null;
  }, [cancelPendingWidthFrame]);

  const scheduleWidthChange = useCallback(
    (nextWidthPx: number) => {
      pendingWidthRef.current = nextWidthPx;
      if (resizeRafRef.current != null) {
        return;
      }

      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        if (pendingWidthRef.current == null) {
          return;
        }

        onWidthChangeRef.current(pendingWidthRef.current);
        pendingWidthRef.current = null;
      });
    },
    []
  );

  useEffect(() => () => cancelPendingWidthFrame(), [cancelPendingWidthFrame]);
  useOutsideCallback(panelRef, onOutsideCallback)

  // Stable references so tab tables do not remount when only the enabled
  // toggle (or panel width) changes. The Tenants tab shows the selected
  // tenant's ConditionalFeatureFlagTenant row; a facility-only flag has none.
  const tenantRows = useMemo<FeatureFlagAssignmentRow[]>(
    () =>
      flag.conditionalFeatureFlagTenantId != null
        ? [
            {
              id: flag.conditionalFeatureFlagTenantId,
              name: flag.tenantName,
              startDate: flag.startDate,
              endDate: flag.endDate,
            },
          ]
        : [],
    [flag.conditionalFeatureFlagTenantId, flag.tenantName, flag.startDate, flag.endDate]
  );

  // Stable handler so the Tenants table's columns (keyed on it) do not
  // rebuild between renders. The tab shows one row — the selected tenant's —
  // so the row argument is implied by the flag.
  const onDeleteTenantAssignment = useCallback(
    () => onDeleteTenantRow(flag.id),
    [onDeleteTenantRow, flag.id]
  );

  const facilityRows = useMemo<FeatureFlagAssignmentRow[]>(
    () =>
      flag.facilities.map((facility) => ({
        id: facility.id,
        name: facility.facilityName,
        startDate: facility.startDate,
        endDate: facility.endDate,
      })),
    [flag.facilities]
  );

  // Maps the clicked row (keyed by the ConditionalFeatureFlagFacility row id)
  // back to its facility id, which is what the delete endpoint expects.
  const onDeleteFacilityAssignment = useCallback(
    (row: FeatureFlagAssignmentRow) => {
      const facility = flag.facilities.find((item) => item.id === row.id);
      if (facility) {
        onDeleteFacilityRow(flag.id, facility.facilityId);
      }
    },
    [flag.facilities, flag.id, onDeleteFacilityRow]
  );

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      event.currentTarget.setPointerCapture(event.pointerId);
      resizeStartRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: widthPx,
      };
    },
    [widthPx]
  );

  const onResizePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = resizeStartRef.current;
      if (!start || start.pointerId !== event.pointerId) {
        return;
      }

      // Left edge: drag left widens; drag right narrows. Gate through rAF so
      // high-frequency pointer moves do not flood setState every pixel.
      scheduleWidthChange(
        clampPanelWidth(start.startWidth + (start.startX - event.clientX))
      );
    },
    [scheduleWidthChange]
  );

  const onResizePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = resizeStartRef.current;
      if (!start || start.pointerId !== event.pointerId) {
        return;
      }

      resizeStartRef.current = null;
      // Apply the latest pending width immediately so the drag end position
      // is not lost if the frame has not fired yet.
      flushPendingWidth();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [flushPendingWidth]
  );

  const onResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 32 : 16;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onWidthChange(clampPanelWidth(widthPx + step));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onWidthChange(clampPanelWidth(widthPx - step));
      }
    },
    [onWidthChange, widthPx]
  );

  return (
    <aside ref={panelRef}
      className="featureFlagDetailsPanel"
      aria-label={`Details for ${flag.name}`}
      style={{ width: widthPx }}
    >
      <div
        className="panelResizeHandle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize details panel"
        aria-valuemin={FEATURE_FLAG_PANEL_WIDTH_MIN_PX}
        aria-valuemax={FEATURE_FLAG_PANEL_WIDTH_MAX_PX}
        aria-valuenow={Math.round(widthPx)}
        tabIndex={0}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
        onKeyDown={onResizeKeyDown}
      />

      <Button
        variant="link"
        size="sm"
        type="button"
        className="panelCloseButton"
        aria-label="Close details panel"
        onClick={onClose}
      >
        <i className="fa fa-times" aria-hidden="true" />
      </Button>

      <h5 className="panelTitle">{flag.name}</h5>

      <div className="detailRow">
        <span className="detailLabel">Details: </span>
        {flag.description}
      </div>
      <div className="detailRow">
        <span className="detailLabel">Start Date: </span>
        {FormatFeatureFlagDate(flag.startDate)}
      </div>
      <div className="detailRow">
        <span className="detailLabel">End Date: </span>
        {FormatFeatureFlagDate(flag.endDate)}
      </div>
      <div className="detailRow enabledRow">
        <span className="detailLabel">Enabled: </span>
        <Form.Check
          type="switch"
          id={`feature-flag-details-enabled-${flag.id}`}
          className="mb-0 enabledSwitch"
          checked={pendingEnabled ?? flag.enabled}
          disabled={flag.conditionalFeatureFlagTenantId == null}
          onChange={(e) => onToggleEnabled(flag.id, e.target.checked)}
          aria-label={`Enabled status for ${flag.name}`}
        />
      </div>
      <div className="detailRow">
        <span className="detailLabel">Created Date: </span>
        {FormatFeatureFlagDate(flag.createdDate)}
      </div>
      <div className="detailRow">
        <span className="detailLabel">Last Modified Date: </span>
        {FormatFeatureFlagDate(flag.lastModifiedDate)}
      </div>

      <div className="panelActions">
        <Button variant="primary" size="sm" type="button" onClick={onEditFlag}>
          Edit
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="button"
          onClick={() => onDeleteFlagForTenant(flag.id)}
        >
          Remove from tenant
        </Button>
      </div>

      {/* Keyed by flag id so tab choice, search, and paging reset when the
          selected flag changes. */}
      <Tabs
        key={flag.id}
        defaultActiveKey="tenants"
        id={`feature-flag-details-tabs-${flag.id}`}
        className="detailsTabs"
      >
        <Tab eventKey="tenants" title="Tenants">
          <FeatureFlagAssignmentTable
            itemLabel="Tenant"
            idPrefix={`flag-${flag.id}-tenants`}
            rows={tenantRows}
            onDeleteRow={onDeleteTenantAssignment}
          />
        </Tab>
        <Tab eventKey="facilities" title="Facilities">
          <FeatureFlagAssignmentTable
            itemLabel="Facility"
            idPrefix={`flag-${flag.id}-facilities`}
            rows={facilityRows}
            onDeleteRow={onDeleteFacilityAssignment}
          />
        </Tab>
      </Tabs>
    </aside>
  );
}

export default FeatureFlagDetailsPanel;
